import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Building2, Globe, Loader2, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useCreateContact } from "../hooks/useContacts";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { NewCompanyDrawer } from "@/modules/companies/components/NewCompanyDrawer";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import type { ContactDetail } from "../types";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (contact: ContactDetail) => void;
  /** Pre-fill the company picker (e.g. when opened from an External company's Contacts tab). */
  defaultCompanyId?: string;
};

export function NewContactDrawer({
  isOpen,
  onClose,
  onCreated,
  defaultCompanyId,
}: Props) {
  const createContact = useCreateContact();
  const companies = useCompanyList({ limit: 100 });
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [department, setDepartment] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [mobile, setMobile] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [type, setType] = useState("External");
  const [status, setStatus] = useState("Active");
  const [linkedIn, setLinkedIn] = useState("");
  const [notes, setNotes] = useState("");
  const [quickCompanyOpen, setQuickCompanyOpen] = useState(false);

  const { data: companyTypes = [] } = useCategoryItems("company_type");
  const typeLabelByValue = useMemo(
    () => new Map(companyTypes.map((t) => [t.value, t.label])),
    [companyTypes],
  );

  const operationalCompanies = useMemo(
    () =>
      (companies.data?.data ?? []).filter((c) => c.category === "OPERATIONAL"),
    [companies.data],
  );
  const externalCompanies = useMemo(
    () =>
      (companies.data?.data ?? []).filter((c) => c.category === "EXTERNAL"),
    [companies.data],
  );

  function reset() {
    setFirstName("");
    setLastName("");
    setJobTitle("");
    setDepartment("");
    setEmail("");
    setPhone("");
    setMobile("");
    setCompanyId(defaultCompanyId ?? "");
    setCompanyName("");
    setType("External");
    setStatus("Active");
    setLinkedIn("");
    setNotes("");
  }

  useEffect(() => {
    if (isOpen) reset();
    else reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, defaultCompanyId]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) return;
    const res = await createContact.mutateAsync({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      mobile: mobile.trim() || undefined,
      jobTitle: jobTitle.trim() || undefined,
      department: department.trim() || undefined,
      companyId: companyId || undefined,
      companyName: companyName.trim() || undefined,
      type,
      status,
      linkedIn: linkedIn.trim() || undefined,
      notes: notes.trim() || undefined,
    });
    onCreated?.(res.contact);
    reset();
    onClose();
  }

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998] bg-black/30" onClick={onClose} />
      <div
        className="fixed right-0 top-0 z-[9999] flex h-full w-[500px] flex-col border-l bg-white shadow-xl"
        style={{ borderColor: "hsl(210,30%,85%)" }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <h2 className="text-lg font-semibold text-foreground">New Contact</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-1 flex-col overflow-y-auto"
        >
          <div className="flex-1 space-y-6 p-6">
            <h3 className="text-sm font-semibold text-foreground">Personal</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ct-fn">
                  First Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="ct-fn"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ct-ln">
                  Last Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="ct-ln"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ct-jt">Job Title</Label>
                <Input
                  id="ct-jt"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ct-dp">Department</Label>
                <Input
                  id="ct-dp"
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                />
              </div>
            </div>

            <h3 className="text-sm font-semibold text-foreground">Contact</h3>
            <div className="space-y-2">
              <Label htmlFor="ct-em">Email</Label>
              <Input
                id="ct-em"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ct-ph">Phone</Label>
                <Input
                  id="ct-ph"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ct-mb">Mobile</Label>
                <Input
                  id="ct-mb"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                />
              </div>
            </div>

            <h3 className="text-sm font-semibold text-foreground">Company</h3>
            <div className="space-y-2">
              <Label htmlFor="ct-co">Company</Label>
              <Select
                id="ct-co"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
              >
                <option value="">— Select company —</option>
                {operationalCompanies.length > 0 && (
                  <optgroup label="🏢 Our Companies">
                    {operationalCompanies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} — {c.types.map((t) => typeLabelByValue.get(t) ?? t).join(", ") || "—"}
                      </option>
                    ))}
                  </optgroup>
                )}
                {externalCompanies.length > 0 && (
                  <optgroup label="🌐 External">
                    {externalCompanies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} — {c.types.map((t) => typeLabelByValue.get(t) ?? t).join(", ") || "—"}
                      </option>
                    ))}
                  </optgroup>
                )}
              </Select>
              {companyId && (
                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  {(() => {
                    const c = companies.data?.data.find(
                      (x) => x.id === companyId,
                    );
                    if (!c) return null;
                    const isOp = c.category === "OPERATIONAL";
                    const Icon = isOp ? Building2 : Globe;
                    return (
                      <>
                        <Icon className="h-3 w-3" strokeWidth={1.5} />
                        <span>
                          {c.name} · {c.types.map((t) => typeLabelByValue.get(t) ?? t).join(", ") || "—"}
                        </span>
                      </>
                    );
                  })()}
                </div>
              )}
              <button
                type="button"
                onClick={() => setQuickCompanyOpen(true)}
                className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
              >
                <Plus className="h-3 w-3" strokeWidth={1.5} />
                New Company
              </button>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ct-cn">Company Name (if not in system)</Label>
              <Input
                id="ct-cn"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="External company name"
              />
            </div>

            <h3 className="text-sm font-semibold text-foreground">Other</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ct-ty">Type</Label>
                <Select
                  id="ct-ty"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                >
                  <option value="External">External</option>
                  <option value="Partner">Partner</option>
                  <option value="Client">Client</option>
                  <option value="Vendor">Vendor</option>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ct-st">Status</Label>
                <Select
                  id="ct-st"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ct-li">LinkedIn</Label>
              <Input
                id="ct-li"
                value={linkedIn}
                onChange={(e) => setLinkedIn(e.target.value)}
                placeholder="https://linkedin.com/in/..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ct-nt">Notes</Label>
              <textarea
                id="ct-nt"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full rounded-md border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(210,100%,50%)]"
                style={{ borderColor: "hsl(210,30%,85%)" }}
              />
            </div>
          </div>

          <div
            className="flex items-center justify-end gap-3 border-t px-6 py-4"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                !firstName.trim() ||
                !lastName.trim() ||
                createContact.isPending
              }
            >
              {createContact.isPending && (
                <Loader2
                  className="mr-1 h-4 w-4 animate-spin"
                  strokeWidth={1.5}
                />
              )}
              Create Contact
            </Button>
          </div>
        </form>
      </div>

      <NewCompanyDrawer
        isOpen={quickCompanyOpen}
        onClose={() => setQuickCompanyOpen(false)}
        category="EXTERNAL"
        titleOverride="New External Company"
        onCreated={(company) => {
          setCompanyId(company.id);
          setCompanyName("");
        }}
      />
    </>,
    document.body,
  );
}
